import {
  OrderPointDto,
  PaymentTypeEnum,
  LocationPointDto,
  OrderPointAreaDto,
  ClientOnlinePaymentDto,
} from '../api/restApi';
import {
  NationwideInfoState,
  OrderDeliveryState,
  OrderPointDeliveryState,
  initialOrderDeliveryState,
  initialOrderPointDeliveryState,
} from './orderSlice';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export enum CustomerTabsEnum {
  Myself = 'myself',
  Another = 'another',
}

export interface OrderAnotherState {
  name: string;
  phone: string;
  noCallNeeded: boolean;
  eatAtThePlace: boolean;
  takeoutAddress?: string;
  leaveAtTheDoor: boolean;
  delivery: OrderDeliveryState;
  orderPointDelivery: OrderPointDeliveryState;
  selectedCustomerTab: CustomerTabsEnum;
}

const initialState: OrderAnotherState = {
  name: '',
  phone: '',
  noCallNeeded: false,
  eatAtThePlace: false,
  leaveAtTheDoor: false,
  delivery: initialOrderDeliveryState,
  orderPointDelivery: initialOrderPointDeliveryState,
  selectedCustomerTab: CustomerTabsEnum.Myself,
};

export const orderAnotherPersonSlice = createSlice({
  name: 'orderAnotherPerson',
  initialState,

  reducers: {
    setAnotherName(state, { payload }: PayloadAction<string>) {
      state.name = payload;
    },
    setAnotherPhone(state, { payload }: PayloadAction<string>) {
      state.phone = payload;
    },
    setAnotherNoCallNeeded(state, { payload }: PayloadAction<boolean>) {
      state.noCallNeeded = payload;
    },
    setAnotherFloor(state, { payload }: PayloadAction<string>) {
      state.delivery.floor = payload;
    },
    setAnotherIntercom(state, { payload }: PayloadAction<string>) {
      state.delivery.intercom = payload;
    },
    setAnotherApartement(state, { payload }: PayloadAction<string>) {
      state.delivery.apartment = payload;
    },
    setAnotherEntranceway(state, { payload }: PayloadAction<string>) {
      state.delivery.entranceway = payload;
    },
    setAnotherPointLabel(state, { payload }: PayloadAction<string>) {
      state.delivery.pointLabel = payload;
    },

    clearAnotherDeliveryAddressDetails(state) {
      state.delivery.city = '';
      state.delivery.street = '';
      state.delivery.house = '';
      state.delivery.floor = '';
      state.delivery.intercom = '';
      state.delivery.apartment = '';
      state.delivery.entranceway = '';
      state.delivery.pointLabel = '';
    },

    setAnotherDeliveryAddress(
      state,
      {
        payload,
      }: PayloadAction<{
        address?: string;
        coordinates?: LocationPointDto;
        city: string;
        street: string;
        house: string;
      }>,
    ) {
      state.delivery.address = payload.address;
      state.delivery.coords = payload.coordinates;
      state.delivery.city = payload.city;
      state.delivery.street = payload.street;
      state.delivery.house = payload.house;
    },

    setAnotherDeliveryAddressComponents(
      state,
      { payload }: PayloadAction<{ city: string; street: string; house: string }>,
    ) {
      state.delivery.city = payload.city;
      state.delivery.street = payload.street;
      state.delivery.house = payload.house;
    },

    setAnotherDeliveryAddressAndCoords(
      state,
      {
        payload,
      }: PayloadAction<{
        address?: string;
        coordinates?: LocationPointDto;
      }>,
    ) {
      state.delivery.address = payload.address;
      state.delivery.coords = payload.coordinates;
    },

    setAnotherDeliveryOrderPointCity(
      state,
      {
        payload,
      }: PayloadAction<{
        city: string;
        fullCity: string;
        coordsCity?: LocationPointDto;
        coordsArea?: OrderPointAreaDto;
      }>,
    ) {
      state.orderPointDelivery.orderPointCity = { city: payload.city, fullVariant: payload.fullCity };
      state.orderPointDelivery.orderPointCityCoords = payload.coordsCity;
      state.orderPointDelivery.orderPointArea = payload.coordsArea;
    },

    setAnotherDeliveryOrderPointAddress(state, { payload }: PayloadAction<string | undefined>) {
      state.orderPointDelivery.orderPointAddress = payload;
    },

    setAnotherUserOrderPoint(state, { payload }: PayloadAction<OrderPointDto | undefined>) {
      state.orderPointDelivery.userOrderPoint = payload;
    },

    setAnotherOrderPointLocationInfo(
      state,
      {
        payload,
      }: PayloadAction<{
        locationId?: string;
        deliveryPrice?: number;
        minCalendarDays?: number;
        maxCalendarDays?: number;
        paymentTypes?: PaymentTypeEnum[];
        onlinePayment?: ClientOnlinePaymentDto;
      }>,
    ) {
      state.orderPointDelivery.locationId = payload?.locationId;
      state.orderPointDelivery.deliveryPrice = payload?.deliveryPrice;
      state.orderPointDelivery.minCalendarDays = payload?.minCalendarDays;
      state.orderPointDelivery.maxCalendarDays = payload?.maxCalendarDays;
      state.orderPointDelivery.paymentTypes = payload?.paymentTypes;
      state.orderPointDelivery.onlinePayment = payload?.onlinePayment;
    },

    clearAnotherOrderPointLocationInfo(state) {
      state.orderPointDelivery.locationId = undefined;
      state.orderPointDelivery.deliveryPrice = undefined;
      state.orderPointDelivery.minCalendarDays = undefined;
      state.orderPointDelivery.maxCalendarDays = undefined;
      state.orderPointDelivery.paymentTypes = undefined;
      state.orderPointDelivery.onlinePayment = undefined;
    },
    clearAnotherDeliveryAddress(state) {
      state.delivery.coords = undefined;
      state.delivery.address = undefined;
    },
    setAreAnotherManualCoordinates(state, { payload }: PayloadAction<boolean>) {
      state.delivery.areManualCoordinates = payload;
    },

    setAnotherNationwideInfo(state, { payload }: PayloadAction<NationwideInfoState | undefined>) {
      state.delivery.nationwideInfo = payload;
    },

    setAnotherDeliveryZone(
      state,
      {
        payload,
      }: PayloadAction<{
        price?: number;
        zoneId?: string;
        locationId?: string;
        paymentTypes?: PaymentTypeEnum[];
        onlinePayment?: ClientOnlinePaymentDto;
      }>,
    ) {
      state.delivery.price = payload.price;
      state.delivery.zoneId = payload.zoneId;
      state.delivery.locationId = payload.locationId;
      state.delivery.paymentTypes = payload.paymentTypes;
      state.delivery.onlinePayment = payload.onlinePayment;
    },

    clearAnotherDeliveryZone(state) {
      state.delivery.price = undefined;
      state.delivery.zoneId = undefined;
      state.delivery.locationId = undefined;
      state.delivery.paymentTypes = undefined;
      state.delivery.onlinePayment = undefined;
    },

    setAnotherTakeoutAddress(state, { payload }: PayloadAction<string | undefined>) {
      state.takeoutAddress = payload;
    },

    changeAnotherLeaveAtTheDoor(state, { payload }: PayloadAction<boolean>) {
      state.leaveAtTheDoor = payload;
    },

    changeAnotherEatAtThePlace(state, { payload }: PayloadAction<boolean>) {
      state.eatAtThePlace = payload;
    },

    setSelectedCustomerTab(state, { payload }: PayloadAction<CustomerTabsEnum>) {
      state.selectedCustomerTab = payload;
    },

    clearOrderAnotherPerson() {
      return initialState;
    },
  },
});

export const {
  setAnotherName,
  setAnotherPhone,
  setAnotherFloor,
  setAnotherIntercom,
  setAnotherApartement,
  setAnotherEntranceway,
  setAnotherPointLabel,
  setAnotherNoCallNeeded,
  setAnotherDeliveryZone,
  setSelectedCustomerTab,
  clearOrderAnotherPerson,
  setAnotherUserOrderPoint,
  setAnotherNationwideInfo,
  clearAnotherDeliveryZone,
  setAnotherTakeoutAddress,
  setAnotherDeliveryAddress,
  changeAnotherEatAtThePlace,
  clearAnotherDeliveryAddress,
  changeAnotherLeaveAtTheDoor,
  setAreAnotherManualCoordinates,
  setAnotherDeliveryOrderPointCity,
  setAnotherOrderPointLocationInfo,
  clearAnotherOrderPointLocationInfo,
  setAnotherDeliveryAddressAndCoords,
  clearAnotherDeliveryAddressDetails,
  setAnotherDeliveryOrderPointAddress,
  setAnotherDeliveryAddressComponents,
} = orderAnotherPersonSlice.actions;

export const orderAnotherPersonReducer = orderAnotherPersonSlice.reducer;
